import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { debounce, isEmpty, map } from 'lodash'
import { Button, Empty, Form, Input, Popconfirm, Rate, Table, Tooltip } from 'antd'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'
import { CalendarOutlined, CaretDownOutlined, CloseCircleOutlined, CommentOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import ThumbUpAltIcon from '@material-ui/icons/ThumbUpAlt'
import ThumbDownAltIcon from '@material-ui/icons/ThumbDownAlt'
import moment from 'moment'

import { ColumnsType, TablePaginationConfig } from 'antd/lib/table'
import { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface'
import { getFormInitialValues, initialize } from 'redux-form'
import { BooleanParam, NumberParam, NumericArrayParam, StringParam, useQueryParams, withDefault } from 'use-query-params'
// eslint-disable-next-line import/no-extraneous-dependencies
import { postInvisibleRating, RatingTableItem, RatingTaskDetail } from '../../reducers/ratings/actions'
import { rateColor } from '../../components/Ratings'
import { RootState } from '../../reducers'
import { FORM } from '../../utils/enums'
import SelectUsersField from '../../components/SelectUsersField'
import queryKeys from '../../queries/queryKeys'
import fetchRatingsList from '../../queries/ratings/fetchRatings'
import DateRangePresetPicker, { DateRangeValue } from '../managerTasks/DateRangePresetPicker'

const PAGE_SIZE = 200

const ACTION_COLOR = '#FFD700'
const ERROR_COLOR = '#F92316'

type Columns = ColumnsType<RatingTableItem>

const renderReaction = (task: RatingTaskDetail) => {
	const isLikeColor = task?.reaction === 'LIKE'
	if (isLikeColor) {
		return <Tooltip placement="top" title={ <span>{ task?.name }</span> }>
			<ThumbUpAltIcon style={ {
				color: ACTION_COLOR,
				marginLeft: 'auto'
			} }/>
		</Tooltip>
	}
	return <Tooltip placement="top" title={ <span>{ task?.name }</span> }><ThumbDownAltIcon style={ {
		color: ERROR_COLOR,
		marginLeft: 'auto'
	} }/></Tooltip>
}

const Ratings = () => {
	const dispatch = useDispatch()
	const queryClient = useQueryClient()

	const { t } = useTranslation('pages')

	const filterInitialValues = useSelector((state: RootState) => getFormInitialValues(FORM.TASKS_FILTER)(state))

	const [query, setQuery] = useQueryParams({
		search: StringParam,
		limit: NumberParam,
		users: withDefault(NumericArrayParam, []),
		ratings: withDefault(NumericArrayParam, []),
		page: withDefault(NumberParam, 1),
		orderBy: withDefault(StringParam, 'createdAt'),
		orderDirection: withDefault(StringParam, 'desc'),
		startDate: StringParam,
		endDate: StringParam,
		showDeleted: withDefault(BooleanParam, false)
	})

	const resetFilter = () => {
		setQuery({
			...query,
			search: null,
			limit: PAGE_SIZE,
			users: [],
			ratings: [],
			page: 1,
			orderBy: 'createdAt',
			orderDirection: 'desc',
			startDate: null,
			endDate: null,
			showDeleted: false
		})
	}

	const activeRatings: number[] = (query.ratings as number[]) ?? []

	const isFilterActive = (query.users?.length ?? 0) > 0
		|| (query.ratings?.length ?? 0) > 0
		|| !!query.startDate
		|| !!query.endDate
		|| !!query.search
		|| !!query.showDeleted

	const toggleRating = (value: number) => {
		const next = activeRatings.includes(value)
			? activeRatings.filter((r) => r !== value)
			: [...activeRatings, value]
		setQuery({ ...query, ratings: next, page: 1 })
	}

	const dateRange: DateRangeValue | null = query.startDate && query.endDate
		? [moment(query.startDate), moment(query.endDate)]
		: null

	const { data: ratingsData, isLoading: isLoadingList } = useQuery({
		queryKey: queryKeys.ratings.list({
			limit: PAGE_SIZE,
			page: query.page,
			search: query.search,
			orderBy: query.orderBy,
			orderDirection: query.orderDirection,
			users: query.users,
			ratings: query.ratings as number[],
			startDate: query.startDate,
			endDate: query.endDate,
			showDeleted: query.showDeleted
		}),
		queryFn: () => fetchRatingsList({
			limit: PAGE_SIZE,
			...query
		}),
		keepPreviousData: true
	})

	const dataSource = ratingsData || { tableData: [], pagination: null }

	useEffect(() => {
		if (!filterInitialValues || isEmpty(filterInitialValues)) {
			const initData = {
				search: query.search || null
			}
			dispatch(initialize(FORM.TASKS_FILTER, initData))
		}
	}, [
		dispatch, filterInitialValues,
		query.search
	])

	const showDeletedActive = !!query.showDeleted

	const columns: Columns = [
		{
			title: i18next.t('pages:Zamestnanec'),
			dataIndex: 'userName',
			key: 'userName',
			ellipsis: true,
			width: 210,
			render: (value) => value || '-'
		},
		{
			title: i18next.t('pages:Domovník'),
			dataIndex: 'trusteeName',
			key: 'trusteeName',
			ellipsis: true,
			width: 200,
			render: (value: string | null) => value || '-'
		},
		{
			title: i18next.t('pages:Hodnotenie'),
			sorter: true,
			dataIndex: 'rating',
			key: 'rating',
			ellipsis: true,
			width: 150,
			render(value: number) {
				return <Rate count={4} style={ {
					color: rateColor(value),
					margin: '0px 12px',
					minWidth: 112
				} } value={ value } disabled/> || '-'
			}
		},
		{
			title: i18next.t('pages:Reakcie'),
			sorter: false,
			dataIndex: 'taskRatings',
			key: 'taskRatings',
			ellipsis: true,
			width: 130,
			render(value: RatingTaskDetail[]) {
				return (<div style={{ whiteSpace: 'break-spaces' }}>
					{map(value, (data) => renderReaction(data))}
				</div>)
			}
		},
		{
			title: i18next.t('pages:Komentár'),
			sorter: false,
			dataIndex: 'comment',
			key: 'comment',
			ellipsis: true,
			width: 80,
			render(value: string) {
				return (value?.length > 0 ? <Tooltip placement="top" title={ <span>{ value }</span> }>
					<Button
						icon={ <CommentOutlined twoToneColor="#52c41a"/> }
						type={ 'link' }
					/>
				</Tooltip> : <div/>)
			}
		},
		{
			title: i18next.t('pages:Zákazka'),
			dataIndex: 'jobName',
			key: 'jobName',
			ellipsis: true,
			width: 210,
			render: (value) => value || '-'
		},
		{
			title: i18next.t('pages:Dátum Realizácie'),
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 210,
			sorter: true,
			render(createdAt) {
				return <span>{createdAt}</span>
			}
		},
		...(showDeletedActive ? [{
			title: i18next.t('pages:Dátum zmazania'),
			dataIndex: 'deletedAt',
			key: 'deletedAt',
			width: 180,
			render: (value: string | null) => value || '-'
		}, {
			title: i18next.t('pages:Zmazal'),
			dataIndex: 'deletedByUserName',
			key: 'deletedByUserName',
			width: 180,
			render: (value: string | null) => value || '-'
		}] : []),
		{
			title: '',
			key: 'operation',
			fixed: 'right',
			width: 50,
			render(text, record) {
				return (
					<Popconfirm
						title={i18next.t('pages:Skutočne chcete skryť hodnotenie?')}
						icon={<QuestionCircleOutlined style={{ color: 'red' }}/>}
						cancelText={i18next.t('pages:Zrušiť')}
						okText={i18next.t('pages:Vymazať')}
						onConfirm={(e) => {
							e?.stopPropagation()
							dispatch(postInvisibleRating(record.id, () => {
								queryClient.invalidateQueries({ queryKey: ['ratings', 'list'] })
							}))
						}}
						onCancel={(e) => e?.stopPropagation()}
						okButtonProps={{
							size: 'small',
							type: 'primary'
						}}
						cancelButtonProps={{
							size: 'small',
							type: 'ghost'
						}}
					>
						<Button
							icon={<DeleteOutlined/>}
							type={'primary'}
							onClick={(e) => e.stopPropagation()}
						/>
					</Popconfirm>
				)
			}
		}
	]

	const handleTableChange = (
		pagination: TablePaginationConfig,
		filters: Record<string,
			FilterValue | null>,
		sorter: SorterResult<RatingTableItem> | SorterResult<RatingTableItem>[],
		extra: TableCurrentDataSource<RatingTableItem>
	) => {
		let order = {}
		if ((sorter as SorterResult<any>)?.order) {
			const sorterLocal = (sorter as SorterResult<any>)
			order = {
				orderBy: sorterLocal.field,
				orderDirection: sorterLocal.order === 'ascend' ? 'asc' : 'desc'
			}
		}

		setQuery({
			limit: PAGE_SIZE,
			...query,
			page: pagination.current,
			...order
		})
	}

	const debounced = useCallback(debounce((searchTerm: string) => setQuery({
		...query,
		search: searchTerm
	}), 300), [query])

	const handleOnChange = ((e: React.ChangeEvent<HTMLInputElement>) => {
		debounced(e.target.value)
	})

	return (
		<div className={ 'page-wrapper' }>
			<div className={ 'flex justify-between' }>
				<Form.Item>
					<Input.Search onChange={ handleOnChange } style={ { width: 300 } } allowClear/>
				</Form.Item>
			</div>
			<div className={'flex'} style={{ flexWrap: 'wrap' }}>
				<Form.Item>
					<SelectUsersField
						onValueChange={(data) => {
							setQuery({
								...query,
								users: data.value.map((item) => (item.value))
							})
						}}
						userValues={query.users}
					/>
				</Form.Item>

				<Form.Item style={{ marginLeft: 8 }}>
					{dateRange === null ? (
						<button
							type="button"
							onClick={() => {
								const def: DateRangeValue = [moment().subtract(27, 'days').startOf('day'), moment().endOf('day')]
								setQuery({ ...query, startDate: def[0].toISOString(), endDate: def[1].toISOString() })
							}}
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 6,
								padding: '0 12px',
								height: 32,
								borderRadius: 6,
								border: '1px solid #d9d9d9',
								background: '#fff',
								fontSize: 13,
								cursor: 'pointer',
								color: '#374151'
							}}
						>
							<CalendarOutlined style={{ fontSize: 13, color: '#9ca3af' }} />
							<span>Dátum</span>
							<CaretDownOutlined style={{ fontSize: 10, color: '#9ca3af' }} />
						</button>
					) : (
						<DateRangePresetPicker
							value={dateRange}
							onChange={(range) => setQuery({ ...query, startDate: range[0].toISOString(), endDate: range[1].toISOString(), page: 1 })}
							onClear={() => setQuery({ ...query, startDate: null, endDate: null, page: 1 })}
						/>
					)}
				</Form.Item>

				<Form.Item style={{ marginLeft: 8 }}>
					<div style={{ display: 'flex', gap: 6, alignItems: 'center', height: 32 }}>
						{[4, 3, 2, 1].map((star) => {
							const isGreen = star >= 3
							const active = activeRatings.includes(star)
							const color = isGreen ? '#389e0d' : '#cf1322'
							const bgActive = isGreen ? '#f6ffed' : '#fff1f0'
							const borderActive = isGreen ? '#b7eb8f' : '#ffa39e'
							return (
								<button
									key={star}
									type="button"
									onClick={() => toggleRating(star)}
									style={{
										display: 'inline-flex',
										alignItems: 'center',
										gap: 4,
										padding: '0 10px',
										height: 28,
										borderRadius: 6,
										border: `1px solid ${active ? borderActive : '#d9d9d9'}`,
										background: active ? bgActive : '#fff',
										fontSize: 12,
										cursor: 'pointer',
										color: active ? color : '#9ca3af',
										fontWeight: active ? 600 : 400,
										transition: 'all 0.15s'
									}}
								>
									{Array.from({ length: star }).map((_, i) => (
										<span key={i} style={{ color: active ? color : '#d9d9d9', fontSize: 13 }}>★</span>
									))}
								</button>
							)
						})}
					</div>
				</Form.Item>

				<Form.Item style={{ marginLeft: 8 }}>
					<button
						type="button"
						onClick={() => setQuery({ ...query, showDeleted: !query.showDeleted, page: 1 })}
						style={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: 6,
							padding: '0 12px',
							height: 32,
							borderRadius: 6,
							border: `1px solid ${showDeletedActive ? '#ffa39e' : '#d9d9d9'}`,
							background: showDeletedActive ? '#fff1f0' : '#fff',
							fontSize: 13,
							cursor: 'pointer',
							color: showDeletedActive ? '#cf1322' : '#374151',
							fontWeight: showDeletedActive ? 600 : 400,
							transition: 'all 0.15s'
						}}
					>
						<DeleteOutlined style={{ fontSize: 12, color: showDeletedActive ? '#cf1322' : '#9ca3af' }} />
						{t('pages:Vymazané hodnotenia')}
					</button>
				</Form.Item>

				{isFilterActive && (
					<Form.Item>
						<Button
							className={'mb-4'}
							style={{ marginLeft: 10 }}
							icon={<CloseCircleOutlined/>}
							onClick={resetFilter}
						>
							{t('pages:Zrušiť filter')}
						</Button>
					</Form.Item>
				)}
			</div>
			<Table
				className={ 'general-table' }
				columns={ columns }
				dataSource={ dataSource.tableData }
				onChange={ handleTableChange }
				style={ { marginTop: 0 } }
				showSorterTooltip={ false }
				pagination={ {
					pageSize: PAGE_SIZE,
					total: dataSource.pagination?.totalCount,
					current: dataSource.pagination?.page,
					showSizeChanger: false
				} }
				loading={ isLoadingList }
				onRow={ (record) => ({
					// onClick: () => history.push(`${ t('paths:jobDetail.path') }/${ get(record, 'id') }`)
				}) }
				locale={ {
					emptyText: (
						<Empty description={ t('pages:Žiadne hodnotenia') }/>
					)
				} }
				size={ 'small' }
			/>
		</div>
	)
}

export default Ratings
